/**
 * <splide-slider> — Splide carousel as a Web Component.
 *
 * Mounts Splide on itself (the element carries the `.splide` structure). Options
 * are passed as a JSON string via `data-options`. Theme-editor aware: jumps to a
 * selected slide block and pauses autoplay while editing; cleans up on disconnect.
 *
 * Splide is loaded globally (splide.min.js) before this module.
 *
 * @example
 *   <splide-slider class="splide" data-options='{"perPage":3,"gap":"16px"}'>
 *     <div class="splide__track"><ul class="splide__list">…</ul></div>
 *   </splide-slider>
 */
class SplideSlider extends HTMLElement {
  connectedCallback() {
    if (typeof window.Splide === 'undefined') {
      console.error('[splide-slider] Splide is not loaded.');
      return;
    }

    this.splide = new window.Splide(this, this.parseOptions()).mount();

    if (window.Shopify?.designMode) {
      this.boundSelect = this.onBlockSelect.bind(this);
      this.boundDeselect = this.onBlockDeselect.bind(this);
      document.addEventListener('shopify:block:select', this.boundSelect);
      document.addEventListener('shopify:block:deselect', this.boundDeselect);
    }
  }

  disconnectedCallback() {
    this.splide?.destroy();
    if (this.boundSelect) {
      document.removeEventListener('shopify:block:select', this.boundSelect);
      document.removeEventListener('shopify:block:deselect', this.boundDeselect);
    }
  }

  parseOptions() {
    try {
      return JSON.parse(this.dataset.options ?? '{}');
    } catch (err) {
      console.error('[splide-slider] Invalid data-options JSON', err);
      return {};
    }
  }

  /** Editor: focus the slide whose block was selected and pause autoplay. */
  onBlockSelect(event) {
    const slide = event.target.closest('.splide__slide');
    if (!slide || !this.contains(slide)) return;
    const slides = Array.from(this.querySelectorAll('.splide__list > .splide__slide'));
    const index = slides.indexOf(slide);
    if (index >= 0) {
      this.splide?.go(index);
      this.splide?.Components.Autoplay?.pause();
    }
  }

  onBlockDeselect() {
    this.splide?.Components.Autoplay?.play();
  }
}

if (!customElements.get('splide-slider')) {
  customElements.define('splide-slider', SplideSlider);
}
